import {
  CALCULATIONS_SET_PARAMETER_VALUE,
  PROJECTS_CRUD_RESTORE_STATE,
  RESET_SYS_PARAMETERS_STATE,
  SAVE_SYS_PARAMETERS_STATE,
  STONE_BED_SET_SEPARATION_DISTANCE,
  SUB_PROJECTS_CRUD_RESTORE_STATE
} from "../lib/types";

/**
 * On initial state we have objects to control the state of the field
 * there:
 * {
 *   value: 0, - actual value for the field
 *   touched: false, - if the field has being touched
 *   active: true, - should be used to persist value on session
 *   transfer: true - if value can be transferred between screens
 *   stamp: 0 - will be used as a counter to validate latest change, will use timestamp
 * }
 */
const initialState = {
  loadedProject: false,
  touchedChambers: false,
  touchedChambersStamp: 0,
  touchedPipes: false,
  touchedPipesStamp: 0,
  touchedAdvancedSettings: false,

  /* parameters for CHAMBERS */
  storageVolume: {
    value: 0,
    touched: false,
    active: true,
    transfer: true,
    stamp: 0,
    sync: true,
    syncTo: "pipeRequiredVolume",
    type: "chamber"
  },
  availableLength: {
    value: 0,
    touched: false,
    active: true,
    transfer: true,
    stamp: 0,
    sync: true,
    syncTo: "pipeAvailableLength",
    type: "chamber"
  },
  availableWidth: {
    value: 0,
    touched: false,
    active: true,
    transfer: true,
    stamp: 0,
    sync: true,
    syncTo: "pipeAvailableWidth",
    type: "chamber"
  },
  porousStoneAbove: { value: 0, touched: false, active: false, transfer: false, stamp: 0, sync: false, syncTo: "", type: "chamber" },
  porousStoneBelow: { value: 0, touched: false, active: false, transfer: false, stamp: 0, sync: false, syncTo: "", type: "chamber" },
  baseStoneElevation: { value: 0, touched: false, active: true, transfer: false, stamp: 0, sync: false, syncTo: "", type: "chamber" },
  averageCover: { value: 0, touched: false, active: false, transfer: false, stamp: 0, sync: false, syncTo: "", type: "chamber" },
  porosityPercent: {
    value: 0,
    touched: false,
    active: true,
    transfer: true,
    stamp: 0,
    sync: true,
    syncTo: "pipeStonePorosity",
    type: "chamber"
  },
  hasOutlet: { value: true, touched: false, active: true, transfer: false, stamp: 0, sync: false, syncTo: "", type: "chamber" },

  hasImperviousLiner: { value: false, touched: false, active: true, transfer: false, stamp: 0, sync: false, syncTo: "", type: "chamber" },

  /* parameters for PIPES */
  pipeRequiredVolume: {
    value: 0,
    touched: false,
    active: true,
    transfer: true,
    stamp: 0,
    sync: true,
    syncTo: "storageVolume",
    type: "pipe"
  },
  pipeAvailableLength: {
    value: 0,
    touched: false,
    active: true,
    transfer: true,
    stamp: 0,
    sync: true,
    syncTo: "availableLength",
    type: "pipe"
  },
  pipeAvailableWidth: {
    value: 0,
    touched: false,
    active: true,
    transfer: true,
    stamp: 0,
    sync: true,
    syncTo: "availableWidth",
    type: "pipe"
  },
  pipeStoneAbove: { value: 0, touched: false, active: false, transfer: false, stamp: 0, sync: false, syncTo: "", type: "pipe" },
  pipeStoneBelow: { value: 0, touched: false, active: false, transfer: false, sync: false, syncTo: "", type: "pipe" },
  pipePerimeterOffset: { value: 0, touched: false, active: true, transfer: false, stamp: 0, sync: false, syncTo: "", type: "pipe" },
  pipeStonePorosity: { value: 0, touched: false, active: true, transfer: true, sync: true, syncTo: "porosityPercent", type: "pipe" },
  pipeNominalDiameter: { value: {}, touched: false, active: true, transfer: false, stamp: 0, sync: false, syncTo: "", type: "pipe" },
  pipeFitting: { value: {}, touched: false, active: true, transfer: false, stamp: 0, sync: false, syncTo: "", type: "pipe" },

  /* parameters for advanced settings */
  sepDistance: { value: 0, touched: false, active: true, transfer: false, stamp: 0, sync: false, syncTo: "", type: "advanced" }
};


/**
 * System Parameters and Advanced settings reducer, to manage state of different fields
 * @param state
 * @param action
 * @returns {*}
 */
export default ( state = initialState, action ) => {
  switch (action.type) {
    case CALCULATIONS_SET_PARAMETER_VALUE:
      try {
        let st = state[action.payload.param];
        st.value = action.payload.value;
        st.touched = true;
        st.stamp = new Date().valueOf();
        return {
          ...state,
          [action.payload.param]: st,
          touchedChambers: true,
          touchedChambersStamp: new Date().valueOf()
        };
      } catch (e) {
        console.error(`system-parameters-reducer.js > CALCULATIONS_SET_PARAMETER_VALUE error: ${e.message}, param: ${action.payload.param}`);
        console.info("state", state);
        return {
          ...state,
          touchedChambers: true,
          touchedChambersStamp: new Date().valueOf()
        };
      }

    case STONE_BED_SET_SEPARATION_DISTANCE:
      let st1 = state["sepDistance"];
      st1.value = action.payload.sepDistance;
      st1.touched = true;
      st1.stamp = new Date().valueOf();
      return {
        ...state,
        sepDistance: st1,
        touchedAdvancedSettings: true
      };

    case SAVE_SYS_PARAMETERS_STATE:
      return {
        ...state
      };

    case RESET_SYS_PARAMETERS_STATE:
      // Cant use the initState, for some reason it is being affected
      // I am sure this happens because it  is using inner objects
      return {
        loadedProject: false,
        touchedChambers: false,
        touchedChambersStamp: 0,
        touchedPipes: false,
        touchedPipesStamp: 0,
        touchedAdvancedSettings: false,

        /* parameters for CHAMBERS */
        storageVolume: {
          value: 0,
          touched: false,
          active: true,
          transfer: true,
          stamp: 0,
          sync: true,
          syncTo: "pipeRequiredVolume",
          type: "chamber"
        },
        availableLength: {
          value: 0,
          touched: false,
          active: true,
          transfer: true,
          stamp: 0,
          sync: true,
          syncTo: "pipeAvailableLength",
          type: "chamber"
        },
        availableWidth: {
          value: 0,
          touched: false,
          active: true,
          transfer: true,
          stamp: 0,
          sync: true,
          syncTo: "pipeAvailableWidth",
          type: "chamber"
        },
        porousStoneAbove: {
          value: 0,
          touched: false,
          active: false,
          transfer: false,
          stamp: 0,
          sync: false,
          syncTo: "",
          type: "chamber"
        },
        porousStoneBelow: {
          value: 0,
          touched: false,
          active: false,
          transfer: false,
          stamp: 0,
          sync: false,
          syncTo: "",
          type: "chamber"
        },
        baseStoneElevation: {
          value: 0,
          touched: false,
          active: true,
          transfer: false,
          stamp: 0,
          sync: false,
          syncTo: "",
          type: "chamber"
        },
        averageCover: {
          value: 0,
          touched: false,
          active: false,
          transfer: false,
          stamp: 0,
          sync: false,
          syncTo: "",
          type: "chamber"
        },
        porosityPercent: {
          value: 0,
          touched: false,
          active: true,
          transfer: true,
          stamp: 0,
          sync: true,
          syncTo: "pipeStonePorosity",
          type: "chamber"
        },
        hasOutlet: {
          value: true,
          touched: false,
          active: true,
          transfer: false,
          stamp: 0,
          sync: false,
          syncTo: "",
          type: "chamber"
        },

        hasImperviousLiner: {
          value: false,
          touched: false,
          active: true,
          transfer: false,
          stamp: 0,
          sync: false,
          syncTo: "",
          type: "chamber"
        },

        /* parameters for PIPES */
        pipeRequiredVolume: {
          value: 0,
          touched: false,
          active: true,
          transfer: true,
          stamp: 0,
          sync: true,
          syncTo: "storageVolume",
          type: "pipe"
        },
        pipeAvailableLength: {
          value: 0,
          touched: false,
          active: true,
          transfer: true,
          stamp: 0,
          sync: true,
          syncTo: "availableLength",
          type: "pipe"
        },
        pipeAvailableWidth: {
          value: 0,
          touched: false,
          active: true,
          transfer: true,
          stamp: 0,
          sync: true,
          syncTo: "availableWidth",
          type: "pipe"
        },
        pipeStoneAbove: {
          value: 0,
          touched: false,
          active: false,
          transfer: false,
          stamp: 0,
          sync: false,
          syncTo: "",
          type: "pipe"
        },
        pipeStoneBelow: { value: 0, touched: false, active: false, transfer: false, sync: false, syncTo: "", type: "pipe" },
        pipePerimeterOffset: {
          value: 0,
          touched: false,
          active: true,
          transfer: false,
          stamp: 0,
          sync: false,
          syncTo: "",
          type: "pipe"
        },
        pipeStonePorosity: {
          value: 0,
          touched: false,
          active: true,
          transfer: true,
          sync: true,
          syncTo: "porosityPercent",
          type: "pipe"
        },
        pipeNominalDiameter: {
          value: {},
          touched: false,
          active: true,
          transfer: false,
          stamp: 0,
          sync: false,
          syncTo: "",
          type: "pipe"
        },
        pipeFitting: { value: {}, touched: false, active: true, transfer: false, stamp: 0, sync: false, syncTo: "", type: "pipe" },

        /* parameters for advanced settings */
        sepDistance: {
          value: 0,
          touched: false,
          active: true,
          transfer: false,
          stamp: 0,
          sync: false,
          syncTo: "",
          type: "advanced"
        }
      };

    case SUB_PROJECTS_CRUD_RESTORE_STATE:
    case PROJECTS_CRUD_RESTORE_STATE:
      /* receive the new state */
      let rState = action.payload.systemParameters;
      rState = {
        ...rState,
        loadedProject: true
      };

      if (Object.keys(rState).length >= 22) {
        return { ...rState };
      } else {
        return { ...initialState, loadedProject: true };
      }

    default:
      return state;
  }
};

// copy of pipe-globals-reducer.js setParameterValue()
/**
 * copy of pipe-globals-reducer.js setParameterValue(),
 * Was altered to save values with state for system parameters
 * @param {Object} currentState
 * @param {string} parameter
 * @param {*} value
 * @returns {*}
 */
const setParameterValue = ( currentState, parameter, value ) => {
  let st = currentState[parameter];
  st.value = value;
  st.touched = true;
  st.stamp = new Date().valueOf();
  switch (parameter) {
    default:
      break;
    case "pipeNominalDiameter":
      const tmpPipeStoneAbove = currentState.pipeStoneAbove;
      tmpPipeStoneAbove.value = 6;
      tmpPipeStoneAbove.touched = true;
      tmpPipeStoneAbove.stamp = new Date().valueOf();

      const tmpPipeStoneBelow = currentState.pipeStoneAbove;
      tmpPipeStoneBelow.value = value.rawPipeND < 30 ? 4 : 6;
      tmpPipeStoneBelow.touched = true;
      tmpPipeStoneBelow.stamp = new Date().valueOf();

      const tmpPipePerimeterOffset = currentState.pipeStoneAbove;
      tmpPipeStoneBelow.value = value.perimeterOffset.default.in;
      tmpPipeStoneBelow.touched = true;
      tmpPipeStoneBelow.stamp = new Date().valueOf();

      return {
        ...currentState,
        pipeStoneAbove: tmpPipeStoneAbove,
        pipeStoneBelow: tmpPipeStoneBelow,
        pipePerimeterOffset: tmpPipePerimeterOffset,
        [parameter]: st,
        touchedPipes: true,
        touchedPipesStamp: new Date().valueOf()
      };
    case "pipeStoneAbove":
    case "pipeStoneBelow":
    case "pipeFitting":
    case "pipeType":
    case "pipeSystemType":
    case "pipeRequiredVolume":
    case "pipeAvailableWidth":
    case "pipeAvailableLength":
    case "pipeStonePorosity":
    case "pipeInvertElevation":
    case "pipeRowSpacing":
    case "pipePerimeterOffset":
      return {
        ...currentState,
        [parameter]: st,
        touchedPipes: true,
        touchedPipesStamp: new Date().valueOf()
      };
  }

  return currentState;
};



// WEBPACK FOOTER //
// ./src/reducers/system-parameters-reducer.js